import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Border_Width = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">📏 CSS Border Width</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>border-width</code> property defines how thick the border of
        an element should be. It works together with <code>border-style</code>{" "}
        and <code>border-color</code> to make the border visible.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock code={`selector { border-width: value; }`} />
        <p>
          Possible values include:
          <br />• <strong>thin</strong>, <strong>medium</strong>,{" "}
          <strong>thick</strong> (browser-defined values).
          <br />• Exact units like <code>px</code>, <code>em</code>, or{" "}
          <code>rem</code>.
        </p>
      </div>

      {/* Basic Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Basic Examples</h2>
        <CodeCopyBlock
          code={`p { border-width: thin; }
p { border-width: medium; }
p { border-width: thick; }
p { border-width: 5px; }`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: "1rem",
          }}
        >
          <div style={{ border: "thin solid green", padding: "10px" }}>
            thin
          </div>
          <div style={{ border: "medium solid green", padding: "10px" }}>
            medium
          </div>
          <div style={{ border: "thick solid green", padding: "10px" }}>
            thick
          </div>
          <div style={{ border: "5px solid green", padding: "10px" }}>5px</div>
        </div>
      </div>

      {/* Individual Sides */}
      <div className="tutorial-section">
        <h2 className="section-title">Individual Sides</h2>
        <p>
          You can set the border width for each side separately using{" "}
          <code>border-top-width</code>, <code>border-right-width</code>,{" "}
          <code>border-bottom-width</code>, and <code>border-left-width</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  border-style: solid;
  border-color: blue;
  border-top-width: 2px;
  border-right-width: 5px;
  border-bottom-width: 8px;
  border-left-width: 12px;
}`}
        />
        <div
          style={{
            borderStyle: "solid",
            borderColor: "blue",
            borderTopWidth: "2px",
            borderRightWidth: "5px",
            borderBottomWidth: "8px",
            borderLeftWidth: "12px",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🔵 Different border widths on each side
        </div>
      </div>

      {/* Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Border-Width Shorthand</h2>
        <p>You can also specify border widths in shorthand using 1–4 values:</p>
        <CodeCopyBlock
          code={`/* 1 value - all sides */
div { border-width: 5px; }

/* 2 values - top/bottom | left/right */
div { border-width: 5px 10px; }

/* 3 values - top | left/right | bottom */
div { border-width: 5px 10px 15px; }

/* 4 values - top | right | bottom | left */
div { border-width: 5px 10px 15px 20px; }`}
        />

        <div
          style={{
            borderStyle: "solid",
            borderColor: "red",
            borderWidth: "5px 10px 15px 20px",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🔴 Shorthand: top 5px | right 10px | bottom 15px | left 20px
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you understand <code>border-width</code>, explore{" "}
          <a href="/css/border-style" className="learn-more">
            CSS Border Style
          </a>{" "}
          to learn how to change the line design of borders.
        </p>
      </div>
    </div>
  );
};

export default Border_Width;
